import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Button } from 'components/UIElements'
import { DEFAULT_LANG } from 'utils/constants'
import LanguageListPopup from './LanguageListPopup'
import { formatStringForID } from 'utils/misc'

const DEFAULT_LIST = [
  {
    key: DEFAULT_LANG,
    value: DEFAULT_LANG,
    text: 'English (US)*',
  },
]

const parseLangKeysLangItemsArr = (arr, localesMap) => {
  return arr.map(langKey => {
    return { key: langKey, value: langKey, text: localesMap[langKey]?.text }
  })
}

const LanguageItem = props => {
  const {
    currentLang,
    deleteConsentTranslation,
    dismissConsentError,
    hasConsentError,
    isDefault,
    language,
    setCurrentLang,
  } = props
  const { value, text } = language
  const isSelected = value === currentLang

  return (
    <div className='translation-lang-wrapper position-relative'>
      <Button
        className={`translation-lang${isSelected ? ' sel' : ''} flexed center-aligned`}
        noStyling
        onMouseDown={e => e.preventDefault()}
        onClick={() => {
          if (isDefault) dismissConsentError()
          setCurrentLang(value)
        }}>
        {isSelected && <i className={`fas fa-caret-right${isDefault ? ' is-default' : ''}`} />}
        <div id={formatStringForID(text)} className='lang-text'>
          {text}
        </div>
      </Button>
      {!isDefault && (
        <Button
          noStyling
          className='rm-translation'
          onMouseDown={e => e.preventDefault()}
          onClick={() => {
            setCurrentLang(DEFAULT_LANG)
            deleteConsentTranslation(value)
          }}>
          <i className='fas fa-trash' />
        </Button>
      )}
      {isDefault && hasConsentError && <i className='fas fa-exclamation-triangle' />}
    </div>
  )
}

const TranslationSelection = props => {
  const { clearAllConsentTranslations, consent, localesMap, setCurrentLang } = props
  const { metadata } = consent
  const { other_languages: otherLanguages } = metadata
  const translationListRef = useRef(null)
  const prevOtherLanguagesLengthRef = useRef(otherLanguages?.length || 0)
  let translationList = DEFAULT_LIST

  if (otherLanguages) translationList = [...DEFAULT_LIST, ...parseLangKeysLangItemsArr(otherLanguages, localesMap)]

  useEffect(() => {
    const currentLength = otherLanguages?.length || 0
    if (currentLength > prevOtherLanguagesLengthRef.current && translationListRef.current) {
      const lastItem = translationListRef.current.lastElementChild
      lastItem?.scrollIntoView({ block: 'nearest' })
    }
    prevOtherLanguagesLengthRef.current = currentLength
  }, [otherLanguages?.length])

  return (
    <div className='translation-selection flexed column space-between-justified'>
      <div className='translation-list' ref={translationListRef}>
        {translationList.map(language => {
          const { key } = language
          return (
            <LanguageItem
              key={key}
              isDefault={key === DEFAULT_LANG}
              language={language}
              {...props}
            />
          )
        })}
      </div>
      <div>
        <LanguageListPopup otherLanguages={otherLanguages} {...props} />
        <Button
          id='clear-all-translations'
          className='clear-all-lang'
          content='Clear all translations'
          link
          onMouseDown={e => e.preventDefault()}
          onClick={() => {
            clearAllConsentTranslations()
            setCurrentLang(DEFAULT_LANG)
          }}
        />
      </div>
    </div>
  )
}

LanguageItem.propTypes = {
  currentLang: PropTypes.string,
  deleteConsentTranslation: PropTypes.func,
  dismissConsentError: PropTypes.func,
  hasConsentError: PropTypes.bool,
  isDefault: PropTypes.bool,
  language: PropTypes.shape({
    text: PropTypes.string,
    value: PropTypes.string,
  }),
  setCurrentLang: PropTypes.func,
}
TranslationSelection.propTypes = {
  clearAllConsentTranslations: PropTypes.func,
  consent: PropTypes.shape({
    metadata: PropTypes.object,
  }),
  localesMap: PropTypes.objectOf(PropTypes.object),
  setCurrentLang: PropTypes.func,
}

export default TranslationSelection
